// Models
import { useStores } from 'models';

// React
import { useCallback } from 'react';

// Interfaces
import {
  IAssessmentPreparationDeleteDocumentAttrs,
  IAssessmentPreparationDocumentDetailAttrs,
  IAssessmentPreparationDocumentPICListAttrs,
  IAssessmentPreparationDocumentTemplateAttrs,
  IAssessmentPreparationDocumentDetailAssessmentAttrs,
  IAssessmentPreparationDownloadDocumentAttrs,
  IAssessmentPreparationGetListAttrs,
  IAssessmentPreparationSubmitAttrs,
  IAssessmentPreparationUploadDocumentAttrs,
  IAssessmentPreparationDocumentConsentAssessmentAttrs,
  IAssessmentPreparationDocumentImageIconsAttrs,
  IAssessmentPreparationDocumentAnswerSheetAttrs,
} from 'interfaces/back-office/assessment/preparation/preparation-view.interface';

const useAssessmentPreparation = () => {
  // Hook
  const {
    assessmentPreparation: {
      assessmentPreparation_loading,
      assessmentPreparation_list,
      assessmentPreparation_DocHeader,
      assessmentPreparation_DocFooter,
      assessmentPreparation_DocTemplate,
      assessmentPreparation_PIC_list,
      assessmentPreparation_DocDetailAssessment,
      assessmentPreparation_ConsentAssessment,
      assessmentPreparation_ImageIcons,
      assessmentPreparation_AnswerSheets,
      assessmentPreparation_CLEAR_LIST,
      assessmentPreparation_fetchList: fetchList,
      assessmentPreparation_uploadDocument: uploadDocument,
      assessmentPreparation_deleteDocument: deleteDocument,
      assessmentPreparation_downloadDocument: downloadDocument,
      assessmentPreparation_submit: submit,
      assessment_fetchDocumentDetail: fetchDocumentDetail,
      assessment_fetchDocumentTemplate: fetchDocumentTemplate,
      assessment_fetchDocumentPICList: fetchDocumentPICList,
      assessment_fetchDocumentDetailAssessment: fetchDocumentDetailAssessment,
      assessment_fetchDocumentConsentAssesment: fetchDocumentConsentAssessment,
      assessment_fetchDocumentImageIcons: fetchDocumentImageIcons,
      assessment_fetchDocumentAnswerSheets: fetchDocumentSheetAnswer,
    },
  } = useStores();

  /**
   * @description Fetch assessment preparation list
   *
   * @param {IAssessmentPreparationGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_fetchList = useCallback(
    (payload: IAssessmentPreparationGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchList],
  );

  /**
   * @description Upload assessment preparation document
   *
   * @param {IAssessmentPreparationUploadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_uploadDocument = useCallback(
    (payload: IAssessmentPreparationUploadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        uploadDocument({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [uploadDocument],
  );

  /**
   * @description Delete assessment preparation document
   *
   * @param {IAssessmentPreparationDeleteDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_deleteDocument = useCallback(
    (payload: IAssessmentPreparationDeleteDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        deleteDocument({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [deleteDocument],
  );

  /**
   * @description Download assessment preparation document
   *
   * @param {IAssessmentPreparationDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_downloadDocument = useCallback(
    (payload: IAssessmentPreparationDownloadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        downloadDocument({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [downloadDocument],
  );

  /**
   * @description Submit assessment preparation
   *
   * @param {IAssessmentPreparationSubmitAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_submit = useCallback(
    (payload: IAssessmentPreparationSubmitAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        submit({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [submit],
  );
  //new
  /**
   * @description Fetch assessment preparation document detail
   *
   * @param {IAssessmentPreparationDocumentDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_fetchDocumentDetail = useCallback(
    (payload: IAssessmentPreparationDocumentDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDocumentDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
          body: { ...payload.body },
        });
      });
    },
    [fetchDocumentDetail],
  );
  /**
   * @description Fetch assessment preparation document template
   *
   * @param {IAssessmentPreparationDocumentTemplateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_fetchDocumentTemplate = useCallback(
    (payload: IAssessmentPreparationDocumentTemplateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDocumentTemplate({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDocumentTemplate],
  );
  /**
   * @description Fetch assessment preparation document PIC list
   *
   * @param {IAssessmentPreparationDocumentPICListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_fetchDocumentPICList = useCallback(
    (payload: IAssessmentPreparationDocumentPICListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDocumentPICList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDocumentPICList],
  );
  /**
   * @description Fetch assessment preparation document Detail Assessment
   *
   * @param {IAssessmentPreparationDocumentPICListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_fetchDocumentDetailAssessment = useCallback(
    (payload: IAssessmentPreparationDocumentDetailAssessmentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDocumentDetailAssessment({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDocumentDetailAssessment],
  );

  const assessmentPreparation_fetchDocumentConsentAssessment = useCallback(
    (payload: IAssessmentPreparationDocumentConsentAssessmentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDocumentConsentAssessment({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDocumentConsentAssessment],
  );

  const assessmentPreparation_fetchDocumentImageIcons = useCallback(
    (payload: IAssessmentPreparationDocumentImageIconsAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDocumentImageIcons({
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDocumentImageIcons],
  );

  /**
   * @description Fetch assessment preparation list
   *
   * @param {IAssessmentPreparationGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessmentPreparation_fetchAnswerList = useCallback(
    (payload: IAssessmentPreparationDocumentAnswerSheetAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDocumentSheetAnswer({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDocumentSheetAnswer],
  );

  return {
    assessmentPreparation_loading,
    assessmentPreparation_list,
    assessmentPreparation_DocHeader,
    assessmentPreparation_DocFooter,
    assessmentPreparation_DocTemplate,
    assessmentPreparation_PIC_list,
    assessmentPreparation_DocDetailAssessment,
    assessmentPreparation_ConsentAssessment,
    assessmentPreparation_ImageIcons,
    assessmentPreparation_AnswerSheets,
    assessmentPreparation_CLEAR_LIST,
    assessmentPreparation_fetchList,
    assessmentPreparation_uploadDocument,
    assessmentPreparation_deleteDocument,
    assessmentPreparation_downloadDocument,
    assessmentPreparation_submit,
    assessmentPreparation_fetchDocumentDetail,
    assessmentPreparation_fetchDocumentTemplate,
    assessmentPreparation_fetchDocumentPICList,
    assessmentPreparation_fetchDocumentDetailAssessment,
    assessmentPreparation_fetchDocumentConsentAssessment,
    assessmentPreparation_fetchDocumentImageIcons,
    assessmentPreparation_fetchAnswerList,
  };
};

export { useAssessmentPreparation };
